import clsx from 'clsx';
import type * as React from 'react';

import type { InlineProps } from '@gitbook/slate-react';
import {
    type OverlayTriggerState,
    Popover,
    PopoverRoot,
    Tooltip,
    usePopoverTriggerRef,
    usePopoverTriggerState,
} from '@gitbook/spine-react';

import { useRestoreFocusFromPopover } from './useRestoreFocusFromPopover';
import { VoidInlineFrame, type VoidInlineFrameProps } from './VoidInlineFrame';

/**
 * Whether an hover highlighting should be shown.
 * - `false` means no highlighting.
 * - `inline` means highlighting it as inline text.
 * - `inline-block` means highlighting the inline as containing elements that are inline-block.
 */
type VoidInlineWithPopoverHoverHighlighting = false | 'inline' | 'inline-block';

type VoidInlineWithPopoverRenderSpan = (props: {
    /** Whether the popover is open. */
    isActive: boolean;
    /** Whether the inline is focused. */
    isFocused: boolean;
    /** Whether the inline is selected. */
    isSelected: boolean;
    /** Open the popover. */
    openPopover: () => void;
}) => React.ReactNode;

type VoidInlineWithPopoverProps = InlineProps &
    Pick<VoidInlineFrameProps, 'containsTextOnly'> & {
        /** Render the span inside the inline. */
        renderSpan: VoidInlineWithPopoverRenderSpan;
        /** Render the content inside the popover. */
        popover: React.ReactNode;
        /**
         * Whether an hover highlighting should be shown.
         * @default 'inline-block'
         */
        hoverHighlighting?: VoidInlineWithPopoverHoverHighlighting;
        /**
         * Alignment of the void inline element.
         *
         * `cap` sizes the inline to the font's cap height and centres it on the cap
         * band, which is what an icon-sized inline needs to sit level with the text
         * without changing the line box.
         *
         * @default 'middle'
         */
        align?: 'baseline' | 'middle' | 'cap';
    };

/**
 * Render an inline void with a popover to edit it.
 */
export function VoidInlineWithPopover(props: VoidInlineWithPopoverProps) {
    const {
        renderSpan,
        popover,
        hoverHighlighting = 'inline-block',
        align = 'middle',
        ...rest
    } = props;
    const { editor } = rest;

    if (editor.readOnly) {
        return (
            <VoidInlineFrame
                {...rest}
                className={clsx('group/voidinline overflow-hidden', alignClass[align])}
            >
                {renderSpan({
                    isActive: false,
                    isFocused: false,
                    isSelected: false,
                    openPopover: () => {},
                })}
            </VoidInlineFrame>
        );
    }

    return <VoidInlineWithPopoverEditable {...props} hoverHighlighting={hoverHighlighting} />;
}

function VoidInlineWithPopoverEditable(
    props: VoidInlineWithPopoverProps & {
        hoverHighlighting: VoidInlineWithPopoverHoverHighlighting;
    }
) {
    const { renderSpan, popover, hoverHighlighting, ...rest } = props;
    const { node, editor } = props;

    const trigger = usePopoverTriggerState();

    // When closing the popover, focus the editor.
    useRestoreFocusFromPopover(trigger, editor, node);

    return (
        <PopoverRoot {...trigger.rootProps}>
            <VoidInlineWithPopoverSpan
                {...rest}
                renderSpan={renderSpan}
                trigger={trigger}
                hoverHighlighting={hoverHighlighting}
            />
            <Popover placement="bottom-start">{popover}</Popover>
        </PopoverRoot>
    );
}

function VoidInlineWithPopoverSpan(
    props: InlineProps &
        Pick<VoidInlineWithPopoverProps, 'align'> & {
            trigger: OverlayTriggerState;
            renderSpan: VoidInlineWithPopoverRenderSpan;
            hoverHighlighting: VoidInlineWithPopoverHoverHighlighting;
        }
) {
    const { trigger, renderSpan, hoverHighlighting, align = 'middle', ...rest } = props;
    const { node, editor, isFocused, isSelected } = rest;

    const ref = usePopoverTriggerRef();
    const openPopover = () => {
        editor.change((change) => {
            change.collapseToStartOf(node);
            change.blur();
        });

        trigger.open();
    };

    const onClick = (event) => {
        event.preventDefault();
        openPopover();
    };

    return (
        <VoidInlineFrame
            ref={ref}
            {...rest}
            isFocused={trigger.isOpen ? false : isFocused}
            isSelected={trigger.isOpen ? false : isSelected}
            onClick={onClick}
            onContextMenu={(event) => {
                event.preventDefault();
                openPopover();
            }}
            className={clsx('group/voidinline cursor-pointer', alignClass[align])}
        >
            {/*
             * The affordance is a padded box around the content, pulled back in by a
             * matching negative margin. It paints past the content while its margin box
             * stays the content's size, so the text beside it is not pushed apart and the
             * line box — which measures an atomic inline by that margin box — is
             * unchanged. Pressing tightens the bleed rather than moving anything.
             *
             * It takes no pointer events: where the bleed sits over neighbouring text, a
             * click there belongs to the text, not to this inline.
             */}
            <span
                className={clsx(
                    'pointer-events-none inline-flex',
                    hoverHighlighting !== false && [
                        'rounded-sm [--bleed:--spacing(1)] group-active/voidinline:[--bleed:--spacing(0.5)]',
                        hoverHighlighting === 'inline'
                            ? 'mx-[calc(var(--bleed)*-1)] px-(--bleed)'
                            : 'm-[calc(var(--bleed)*-1)] p-(--bleed)',
                        trigger.isOpen || isSelected
                            ? 'bg-block-highlight'
                            : 'group-hover/voidinline:bg-inactive group-active/voidinline:bg-inactive',
                    ]
                )}
            >
                <Tooltip
                    label="Click to edit"
                    delay={300}
                    offset={hoverHighlighting === 'inline-block' ? 8 : 4}
                >
                    {/* Hit target and tooltip trigger, sized to the content, not the bleed. */}
                    <span className="pointer-events-auto inline-flex">
                        {renderSpan({
                            isActive: trigger.isOpen,
                            isFocused,
                            isSelected,
                            openPopover,
                        })}
                    </span>
                </Tooltip>
            </span>
        </VoidInlineFrame>
    );
}

const alignClass = {
    middle: 'align-middle',
    baseline: 'align-baseline',
    cap: 'inline-icon',
};
